/**
 * The browser's half of the session: where the token lives, how it arrives, and what the
 * SPA is allowed to conclude from it.
 *
 * WHAT THE SPA MAY CONCLUDE: what to draw. Nothing else. The claims are read WITHOUT
 * verifying the signature - the browser has no key and could not verify one if it had -
 * so a user who edits their own sessionStorage can make this file say `role: "admin"`.
 * That changes which buttons appear and changes nothing about what comes back from the
 * database, because the sidecar verifies the same token against the Worker's key and it is
 * THAT token's role - not this one - that decides the Postgres role the query runs as.
 * Editing `role` to `admin` here does not turn a `player` session into a staff one: the
 * signed copy still says `player`, so the sidecar still switches to `web_player`, and
 * `web_player` still cannot name the staff schema. Any UI built here that implies otherwise
 * is a lie to its own user.
 *
 * WHY sessionStorage AND NOT localStorage: the token lives fifteen minutes and grants
 * read access to player data. Scoping it to the tab means closing the tab ends the
 * session, and a shared machine does not hand the next person a warm login.
 */
import {
  AUTH_ERROR_BINDING_UNAVAILABLE,
  AUTH_ERROR_NOT_LINKED,
  ERROR_FRAGMENT_KEY,
  TOKEN_FRAGMENT_KEY,
  decodeJwtPayloadWithoutVerifying,
  isEditorJwtClaims,
  type EditorJwtClaims,
} from '@dimensionscore/shared';

export const TOKEN_STORAGE_KEY = 'dc.token';

export interface Session {
  readonly token: string;
  /** Decoded, NOT verified. See the file header. */
  readonly claims: EditorJwtClaims;
}

/** Narrow storage surface so tests pass a plain object instead of faking a Storage. */
export type SessionStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Parses a token into a session, refusing anything malformed or already expired.
 *
 * Expiry is checked here rather than on use so a stale tab shows "signed out" instead of
 * a page of 401s.
 */
export function sessionFromToken(token: string, nowSeconds: number = Math.floor(Date.now() / 1000)): Session | null {
  const payload = decodeJwtPayloadWithoutVerifying(token);
  if (payload === null || !isEditorJwtClaims(payload)) return null;
  if (payload.exp <= nowSeconds) return null;
  return { token, claims: payload };
}

export function readStoredSession(storage: SessionStorageLike, nowSeconds?: number): Session | null {
  let raw: string | null;
  try {
    raw = storage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // Private-mode browsers throw on storage access; signed out is a fine answer.
    return null;
  }
  if (raw === null) return null;
  const session = sessionFromToken(raw, nowSeconds);
  if (session === null) clearStoredSession(storage);
  return session;
}

export function storeSession(storage: SessionStorageLike, token: string): void {
  try {
    storage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // Non-fatal: the session still works for this page load.
  }
}

export function clearStoredSession(storage: SessionStorageLike): void {
  try {
    storage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* nothing to undo */
  }
}

export interface AuthFragment {
  readonly token: string | null;
  readonly error: string | null;
}

/**
 * Reads the `#dc_token=...` the Worker redirected back with.
 *
 * The caller is expected to strip the fragment immediately afterwards: a credential in
 * the address bar ends up in screenshots, in the back-forward history, and in whatever
 * the user pastes into a bug report.
 */
export function parseAuthFragment(hash: string): AuthFragment | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.length === 0) return null;
  const params = new URLSearchParams(raw);
  const token = params.get(TOKEN_FRAGMENT_KEY);
  const error = params.get(ERROR_FRAGMENT_KEY);
  if (token === null && error === null) return null;
  return { token, error };
}

/** Human wording for the stable codes the sidecar's sign-in routes put in `dc_error`. */
export function describeAuthError(code: string): string {
  switch (code) {
    case 'not_authorised':
      return 'That Discord account holds none of the roles this editor is configured for. Ask an administrator for the right guild role.';
    /*
     * THE LAST REFUSAL LEFT ON THIS PATH, AND IT IS NARROWER THAN IT WAS. A Discord account that is
     * a member of the guild and simply has not linked yet is no longer refused at all: it gets an
     * `unlinked` session and `/me` prints the instructions, because entering the code needs a
     * session and telling somebody to do the impossible is worse than saying nothing. What is left
     * is the account that is in neither the guild nor the bindings, so the wording names both halves
     * — and, like the old one, names something the reader can actually go and do.
     */
    case AUTH_ERROR_NOT_LINKED:
      return 'That Discord account is not linked to a Minecraft account, and it is not a member of this server s Discord. Join the Discord, then run /link in game and enter the code it gives you.';
    case AUTH_ERROR_BINDING_UNAVAILABLE:
      return 'We could not check whether that Discord account is linked — the server that knows was not reachable. Try again in a moment; do not run /link yet.';
    case 'no_roles_configured':
      return 'The editor is deployed without any Discord role mapped, so nobody can sign in yet. Set DISCORD_ROLE_IDS_* on the backend sidecar.';
    case 'access_denied':
      return 'You declined the Discord authorisation.';
    case 'bad_state':
      return 'That sign-in link had expired or was not started in this browser. Try again.';
    case 'discord_unavailable':
      return 'Discord did not answer. Try again in a moment.';
    case 'missing_code':
      return 'Discord returned no authorisation code.';
    default:
      return `Sign-in failed (${code}).`;
  }
}

/** URL of the Worker route that starts the OAuth flow, returning to `returnTo` after. */
export function loginUrl(apiBase: string, returnTo: string): string {
  const base = apiBase.replace(/\/+$/, '');
  return `${base}/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}
